import type { Customer, EmergencyContact } from "../../types/customer";
import { buildPhoneMatchKeys, normalizePhoneNumber } from "../../utils/phone";

export type CustomerPhoneMatchType = "customer" | "emergency-contact";

export type CustomerPhoneMatch = {
  customerId: string;
  customerName: string;
  matchType: CustomerPhoneMatchType;
  matchedPhone: string;
  normalizedCaller: string;
  emergencyContactId?: string;
  emergencyContactName?: string;
};

type DirectoryEntry = {
  customerId: string;
  customerName: string;
  phone: string;
  matchType: CustomerPhoneMatchType;
  emergencyContactId?: string;
  emergencyContactName?: string;
  keys: Set<string>;
};

function buildCustomerName(customer: Customer) {
  return [customer.firstName, customer.lastName].filter(Boolean).join(" ").trim();
}

function buildEmergencyContactName(contact: EmergencyContact) {
  return [contact.firstName, contact.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
}

function addEntry(
  entries: DirectoryEntry[],
  customer: Customer,
  phone: string | null | undefined,
  matchType: CustomerPhoneMatchType,
  emergencyContact?: EmergencyContact
) {
  if (!phone) return;
  const keys = buildPhoneMatchKeys(phone);
  if (keys.size === 0) return;
  entries.push({
    customerId: customer.id,
    customerName: buildCustomerName(customer) || "Customer",
    phone,
    matchType,
    emergencyContactId: emergencyContact?.id,
    emergencyContactName: emergencyContact
      ? buildEmergencyContactName(emergencyContact)
      : undefined,
    keys,
  });
}

export function buildCustomerPhoneDirectory(
  customers: Customer[],
  contactsByCustomerId?: Record<string, EmergencyContact[]>
) {
  const entries: DirectoryEntry[] = [];

  for (const customer of customers) {
    const customerWithPhone = customer as Customer & {
      phone?: string | null;
      telephone?: string | null;
    };
    addEntry(entries, customer, customer.mobile, "customer");
    addEntry(entries, customer, customerWithPhone.phone, "customer");
    addEntry(entries, customer, customerWithPhone.telephone, "customer");

    const contacts =
      contactsByCustomerId?.[customer.id] ?? customer.emergencyContacts ?? [];
    for (const contact of contacts) {
      addEntry(entries, customer, contact.phone, "emergency-contact", contact);
    }
  }

  return entries;
}

type CallCustomerIdentity = {
  customerId?: string | null;
  customerName?: string | null;
  callerNumber?: string | null;
};

export type ResolvedCallCustomerIdentity = {
  customerId: string | null;
  customerName: string | null;
  matchType: CustomerPhoneMatchType | null;
};

/** Prefer server-enriched customer identity; fall back to phone directory lookup. */
export function resolveCallCustomerIdentity(
  call: CallCustomerIdentity,
  directory: ReturnType<typeof buildCustomerPhoneDirectory>
): ResolvedCallCustomerIdentity {
  if (call.customerId?.trim() || call.customerName?.trim()) {
    return {
      customerId: call.customerId ?? null,
      customerName: call.customerName ?? null,
      matchType: null,
    };
  }

  const matched = findCustomerForCallerNumber(call.callerNumber, directory);
  if (!matched) {
    return {
      customerId: null,
      customerName: null,
      matchType: null,
    };
  }

  return {
    customerId: matched.customerId,
    customerName: matched.customerName,
    matchType: matched.matchType,
  };
}

export function findCustomerForCallerNumber(
  callerNumber: string | null | undefined,
  directory: ReturnType<typeof buildCustomerPhoneDirectory>
): CustomerPhoneMatch | null {
  const callerKeys = buildPhoneMatchKeys(callerNumber);
  if (!callerNumber || callerKeys.size === 0) return null;

  const ordered = [...directory].sort((a, b) => {
    if (a.matchType === b.matchType) return 0;
    return a.matchType === "customer" ? -1 : 1;
  });

  for (const entry of ordered) {
    for (const key of callerKeys) {
      if (!entry.keys.has(key)) continue;
      return {
        customerId: entry.customerId,
        customerName: entry.customerName,
        matchType: entry.matchType,
        matchedPhone: entry.phone,
        normalizedCaller: normalizePhoneNumber(callerNumber),
        emergencyContactId: entry.emergencyContactId,
        emergencyContactName: entry.emergencyContactName,
      };
    }
  }

  return null;
}
